import React from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFavoritesContext } from '@/components/providers/FavoritesProvider';
import { useToast } from '@/hooks/use-toast';

interface FavoriteButtonProps {
  propertyId: number | string;
  propertyData: {
    title: string;
    image?: string;
    price?: string;
    address?: string;
  };
  variant?: 'default' | 'minimal' | 'icon-only';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showToast?: boolean;
  onToggle?: (isFavorite: boolean) => void;
}

/**
 * Botão para adicionar/remover imóveis dos favoritos
 */
export function FavoriteButton({
  propertyId,
  propertyData,
  variant = 'default',
  size = 'md',
  className,
  showToast = true,
  onToggle,
}: FavoriteButtonProps) {
  const { toggleFavorite, isFavorite } = useFavoritesContext();
  const { toast } = useToast();
  const [isAnimating, setIsAnimating] = React.useState(false);

  const favorite = isFavorite(propertyId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Animação
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    // Toggle favorito
    const nowFavorite = toggleFavorite({
      id: propertyId,
      ...propertyData,
    });

    // Callback
    onToggle?.(nowFavorite);

    // Toast
    if (showToast) {
      toast({
        title: nowFavorite ? '❤️ Adicionado aos favoritos' : '💔 Removido dos favoritos',
        description: nowFavorite 
          ? 'Imóvel salvo na sua lista de favoritos'
          : 'Imóvel removido da sua lista',
        duration: 2000,
      });
    }
  };

  // Estilos por tamanho
  const sizeStyles = {
    sm: 'w-8 h-8 p-1.5',
    md: 'w-10 h-10 p-2',
    lg: 'w-12 h-12 p-2.5',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  // Variantes
  if (variant === 'icon-only') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'relative flex items-center justify-center rounded-full',
          'bg-white/90 hover:bg-white backdrop-blur-sm',
          'border border-gray-200',
          'transition-all duration-200',
          'hover:scale-110 active:scale-95',
          isAnimating && 'scale-125',
          sizeStyles[size],
          className
        )}
        aria-label={favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        title={favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      >
        <Heart
          size={iconSizes[size]}
          className={cn(
            'transition-all duration-200',
            favorite ? 'fill-red-500 text-red-500' : 'text-gray-600'
          )}
        />
      </button>
    );
  }

  if (variant === 'minimal') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full',
          'text-sm font-medium',
          'transition-all duration-200',
          'hover:bg-gray-100',
          isAnimating && 'scale-105',
          favorite ? 'text-red-500' : 'text-gray-600',
          className
        )}
      >
        <Heart
          size={16}
          className={cn(
            'transition-all duration-200',
            favorite && 'fill-red-500'
          )}
        />
        {favorite ? 'Salvo' : 'Salvar'}
      </button>
    );
  }

  // Variant default
  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-lg',
        'font-medium transition-all duration-200',
        'border-2',
        isAnimating && 'scale-105',
        favorite
          ? 'bg-red-50 border-red-500 text-red-600 hover:bg-red-100'
          : 'bg-white border-gray-300 text-gray-700 hover:border-red-500 hover:text-red-600',
        className
      )}
    >
      <Heart
        size={20}
        className={cn(
          'transition-all duration-200',
          favorite && 'fill-red-500'
        )}
      />
      {favorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
    </button>
  );
}

/**
 * Badge com contador de favoritos
 */
export function FavoritesBadge({ className }: { className?: string }) {
  const { count } = useFavoritesContext();

  if (count === 0) return null;

  return (
    <span
      className={cn(
        'absolute -top-1 -right-1',
        'flex items-center justify-center',
        'w-5 h-5 rounded-full',
        'bg-red-500 text-white text-xs font-bold',
        'animate-in zoom-in',
        className
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
